import { isBefore, parseISO, startOfToday } from 'date-fns'
import { type Fn } from 'src/hooks/locale/utils'
import { CRITERIA_TYPE, FINAL_EVALUATION_MIN_RATIO } from './constants'

/**
 * Shared by the admin and lecturer scores pages. The API rejects every breach below with a 422, but
 * with untranslated or misleading messages (and legacy silently rewrote a bad final minimum to 1),
 * so each one is caught here and shown on the offending field before anything is sent.
 */

/** The error for a score cell outside `0…max`, or null. An empty cell is a 0 to the backend. */
export function scoreError(raw: string | number | null | undefined, max: number, t: Fn): string | null {
  const text = raw == null ? '' : String(raw).trim()
  if (text === '') return null
  const value = Number(text)
  if (Number.isNaN(value) || value < 0 || value > max) {
    return `${t('course:score_must_be_within')} 0–${max}`
  }
  return null
}

interface CriteriaBandErrors {
  readonly min: readonly string[]
  readonly max: readonly string[]
}

/**
 * Errors for one paper criterion's min/max. The remediation exam mirrors the final evaluation, so
 * it is never checked on its own.
 */
export function criteriaBandErrors(
  type: number,
  minRaw: string | number,
  maxRaw: string | number,
  t: Fn
): CriteriaBandErrors {
  if (type === CRITERIA_TYPE.REMEDIATION_EXAM) return { min: [], max: [] }

  const min = Number(minRaw)
  const max = Number(maxRaw)
  const minErrors: string[] = []
  const maxErrors: string[] = []

  if (!(max >= 1)) {
    maxErrors.push(t('course:maxsimum_value'))
  }

  if (type === CRITERIA_TYPE.FINAL_EVALUATION) {
    if (!(min > 0)) {
      minErrors.push(t('course:final_evaluation_min_must_exceed_zero'))
    } else if (max >= 1 && min > max * FINAL_EVALUATION_MIN_RATIO) {
      minErrors.push(t('course:final_evaluation_min_exceeds_60_percent'))
    }
  } else if (!(min >= 0)) {
    minErrors.push(t('course:criteria_min_must_not_be_negative'))
  } else if (max >= 1 && min > max) {
    minErrors.push(t('course:criteria_min_exceeds_max'))
  }

  return { min: minErrors, max: maxErrors }
}

/** Score entry stays open through the deadline day itself; legacy locks the column after it. */
export function isDeadlinePassed(deadline: string | null | undefined): boolean {
  if (deadline == null || deadline === '') return false
  return isBefore(parseISO(deadline), startOfToday())
}

/** The backend's untranslated 422 for a score posted after the criterion's deadline. */
export const DEADLINE_MISSED_MESSAGE = 'app.deadline_missed'
